import React from 'react';
import 'whatwg-fetch';
import clsx from 'clsx';
import { AiOutlineMenuFold as MenuFoldIcon } from '@react-icons/all-files/ai/AiOutlineMenuFold';
import { AiOutlineMenuUnfold as MenuUnfoldIcon } from '@react-icons/all-files/ai/AiOutlineMenuUnfold';
import { FaRegEye as DashboardIcon } from '@react-icons/all-files/fa/FaRegEye';
import { FaTrophy as ClassementIcon } from '@react-icons/all-files/fa/FaTrophy';
import { MdEvent as CalendarIcon } from '@react-icons/all-files/md/MdEvent';
import { MdInsertChart as StatIcon } from '@react-icons/all-files/md/MdInsertChart';
import { IoIosArchive as StockIcon } from '@react-icons/all-files/io/IoIosArchive';
import { MdWork as DealIcon } from '@react-icons/all-files/md/MdWork';
import { HiTicket as TicketIcon } from '@react-icons/all-files/hi/HiTicket';
import { MdPermContactCalendar as CarnetIcon } from '@react-icons/all-files/md/MdPermContactCalendar';
import { MdSettings as SettingIcon } from '@react-icons/all-files/md/MdSettings';
import { MdChat as RequestIcon } from '@react-icons/all-files/md/MdChat';
import { MdNote as InterventionOrderIcon } from '@react-icons/all-files/md/MdNote';
import { MdLocalShipping as DeliveryOrderIcon } from '@react-icons/all-files/md/MdLocalShipping';
import { MdHome as HomeIcon } from '@react-icons/all-files/md/MdHome';
import MenuItem from './components/MenuItem';
import StatCard from './components/StatCard';
import { createUseStyles } from 'src/theming';
import logo from 'src/images/logoAlpha.png';
import { MenuType } from 'src/hooks/useDrawer';
import Hidden from '../Hidden';
import { useRecoilValue } from 'recoil';
import { unreadNotificationsByLinkTypeState } from 'src/recoil/selectors/notifications';
import { LinkType } from 'src/interfaces/Notification';
import { APP_VERSION } from 'src/env';
import { Box } from '@material-ui/core';

const useStyles = createUseStyles((theme) => ({
  root: {
    position: 'fixed',
    bottom: 0,
    top: 0,
    width: ({ collapsed }) => theme.components.Drawer[collapsed ? 'collapseWidth' : 'defaultWidth'],
    backgroundColor: '#313a46',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
    zIndex: 30,
    transition: '250ms',
    marginLeft: ({ mobileMode, collapsed }) => (mobileMode && collapsed ? -theme.components.Drawer.defaultWidth : 0),
  },
  sizeTransition: {
    transition:
      'width 0.5s cubic-bezier(.31,.71,.17,.96), height 0.5s  cubic-bezier(.31,.71,.17,.96), padding 0.5s  cubic-bezier(.31,.71,.17,.96)',
    position: 'absolute',
  },
  appName: {
    '-webkit-transform': ({ collapsed }) => `rotateY(${collapsed ? -90 : 0}deg)`,
    transform: ({ collapsed }) => `rotateY(${collapsed ? -90 : 0}deg)`,
    opacity: ({ collapsed }) => (collapsed ? 0 : 1),
    transformOrigin: '0 0',
    transition: 'all .3s ease-in-out',
    color: 'white',
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 6,
    fontSize: 22,
    '& svg': {
      cursor: 'pointer',
    },
  },
  stats: {
    opacity: ({ collapsed }) => (collapsed ? '0' : '1'),
    marginTop: '2rem',
    transition: 'all .3s ease-in-out',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: ({ collapsed }) => `1rem ${collapsed ? 1.5 : 2}rem`,
    marginBottom: '1rem',
    borderBottom: '1px solid #FFFFFF0D',
    '& img': {
      height: 32,
      width: 32,
      marginRight: 8,
    },
  },
}));

interface Props {
  collapsed?: boolean;
  mobileMode?: boolean;
  onChangeMenuType: (type: MenuType) => void;
  type: MenuType;
}

const Sidemenu = React.forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
  const { collapsed, type = 'condensed', onChangeMenuType, mobileMode, ...otherProps } = props;
  const requestNotifications = useRecoilValue(unreadNotificationsByLinkTypeState(LinkType.request));
  const canCollapse = type !== 'fixed' || mobileMode;
  const collapsedItem = canCollapse ? collapsed : false;

  const classes = useStyles({ collapsed: collapsedItem, mobileMode });

  const handleChangeMenuType = () => {
    onChangeMenuType(!canCollapse ? 'condensed' : 'fixed');
  };

  const MenuIcon = !canCollapse ? MenuFoldIcon : MenuUnfoldIcon;

  return (
    <div ref={ref} className={clsx(classes.root, classes.sizeTransition)} {...otherProps}>
      <div className={classes.header}>
        <img src={logo} alt='logo' />
        <div className={classes.appName}>
          Scaleed
          <Hidden lgDown>
            <MenuIcon style={{ fill: '#ffffff91' }} size={20} onClick={handleChangeMenuType} />
          </Hidden>
        </div>
      </div>

      <MenuItem link='/dashboard/home' icon={HomeIcon} label='Accueil' collapsed={collapsedItem} />

      <MenuItem link='/dashboard' icon={DashboardIcon} label='Dashboard' collapsed={collapsedItem} />

      <MenuItem link='/dashboard/panorama/deal' icon={DealIcon} label='Affaires' collapsed={collapsedItem} />

      <MenuItem link='/dashboard/panorama/ticket' icon={TicketIcon} label='Tickets' collapsed={collapsedItem} />

      <MenuItem
        link='/dashboard/requests'
        notificationTotal={requestNotifications.length}
        icon={RequestIcon}
        label='Demandes'
        collapsed={collapsedItem}
      />

      <MenuItem
        link='/dashboard/intervention-orders'
        icon={InterventionOrderIcon}
        label="Bon&nbsp;d'intervention"
        collapsed={collapsedItem}
      />

      <MenuItem
        link='/dashboard/delivery-orders'
        icon={DeliveryOrderIcon}
        label='Bon&nbsp;de&nbsp;livraison'
        collapsed={collapsedItem}
      />

      <MenuItem link='/dashboard/planning' icon={CalendarIcon} label='Planning' collapsed={collapsedItem} />

      <MenuItem link='/dashboard/crm/customers' icon={CarnetIcon} label='Clients' collapsed={collapsedItem} />

      <MenuItem link='/dashboard/stock' icon={StockIcon} label='Stock' collapsed={collapsedItem} />

      <MenuItem link='/dashboard/stats' icon={StatIcon} label='Stats' collapsed={collapsedItem} />

      <MenuItem
        link='/dashboard/rankings/commercial/telecom'
        icon={ClassementIcon}
        label='Classements'
        collapsed={collapsedItem}
      />

      <MenuItem link='/dashboard/book' icon={CarnetIcon} label='Carnet' collapsed={collapsedItem} />

      <MenuItem link='/dashboard/settings' icon={SettingIcon} label='Réglages' collapsed={collapsedItem} />

      <StatCard className={classes.stats} />

      <Box color='#8391a28c' textAlign='center' my={1} visibility={collapsed ? 'hidden' : 'visible'}>
        v-{APP_VERSION}
      </Box>
    </div>
  );
});

export default Sidemenu;
